import React, { useState, useEffect } from 'react';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/SearchInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  StickyNote,
  Plus,
  Edit3,
  Archive,
  ArchiveRestore,
  Trash2,
  Calendar,
  User,
  Users,
  CheckCircle2,
  Circle,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import HivePadModal from '@/components/hivepad/HivePadModal';
import { format } from 'date-fns';

interface HiveNote {
  id: string;
  title: string;
  description: string | null;
  family_id: string | null;
  student_id: string | null;
  due_date: string | null;
  completed: boolean;
  archived: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_by: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  families?: { id: string; name: string } | null;
  students?: { id: string; first_name: string; last_name: string } | null;
}

type FilterStatus = 'active' | 'archived' | 'all';
type SortOption = 'due_date_asc' | 'due_date_desc' | 'created_desc' | 'created_asc' | 'title_asc' | 'title_desc' | 'priority_desc' | 'priority_asc';

interface FilterState {
  status: FilterStatus;
  hasFamily: boolean | null;
  hasStudent: boolean | null;
  hasDueDate: boolean | null;
  completed: boolean | null;
  priority: ('low' | 'medium' | 'high' | 'urgent')[];
}

// Priority helper functions
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getPriorityWeight = (priority: string) => {
  switch (priority) {
    case 'urgent': return 4;
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 2;
  }
};

const HivePad: React.FC = () => {
  const { organizationId } = useAuth();
  const [notes, setNotes] = useState<HiveNote[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<HiveNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('due_date_asc');
  const [filters, setFilters] = useState<FilterState>({
    status: 'active',
    hasFamily: null,
    hasStudent: null,
    hasDueDate: null,
    completed: null,
    priority: [],
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editNote, setEditNote] = useState<HiveNote | null>(null);
  const [deleteNote, setDeleteNote] = useState<HiveNote | null>(null);

  useEffect(() => {
    if (organizationId) {
      loadNotes();
    }
  }, [organizationId]);

  useEffect(() => {
    filterAndSortNotes();
  }, [notes, searchQuery, filters, sortOption]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('hive_notes')
        .select(`
          *,
          families (id, name),
          students (id, first_name, last_name)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Failed to load HivePad notes');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortNotes = () => {
    let filtered = notes;

    // Filter by status
    if (filters.status === 'active') {
      filtered = filtered.filter(note => !note.archived);
    } else if (filters.status === 'archived') {
      filtered = filtered.filter(note => note.archived);
    }

    // Filter by completion status
    if (filters.completed !== null) {
      filtered = filtered.filter(note => note.completed === filters.completed);
    }

    // Filter by family association
    if (filters.hasFamily !== null) {
      filtered = filtered.filter(note => 
        filters.hasFamily ? note.family_id !== null : note.family_id === null
      );
    }

    // Filter by student association
    if (filters.hasStudent !== null) {
      filtered = filtered.filter(note => 
        filters.hasStudent ? note.student_id !== null : note.student_id === null
      );
    }

    // Filter by due date
    if (filters.hasDueDate !== null) {
      filtered = filtered.filter(note => 
        filters.hasDueDate ? note.due_date !== null : note.due_date === null
      );
    }

    // Filter by priority
    if (filters.priority.length > 0) {
      filtered = filtered.filter(note => 
        filters.priority.includes(note.priority)
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(note =>
        note.title.toLowerCase().includes(query) ||
        (note.description && note.description.toLowerCase().includes(query)) ||
        (note.families?.name && note.families.name.toLowerCase().includes(query)) ||
        (note.students && `${note.students.first_name} ${note.students.last_name}`.toLowerCase().includes(query))
      );
    }

    // Sort notes
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'due_date_asc':
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        
        case 'due_date_desc':
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(b.due_date).getTime() - new Date(a.due_date).getTime();
        
        case 'created_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        
        case 'created_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        
        case 'title_asc':
          return a.title.localeCompare(b.title);
        
        case 'title_desc':
          return b.title.localeCompare(a.title);
        
        case 'priority_desc':
          return getPriorityWeight(b.priority) - getPriorityWeight(a.priority);
        
        case 'priority_asc':
          return getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
        
        default:
          return 0;
      }
    });

    setFilteredNotes(filtered);
  };

  const handleToggleComplete = async (noteId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('hive_notes')
        .update({ completed: !currentStatus })
        .eq('id', noteId);

      if (error) throw error;
      
      setNotes(prev => prev.map(note => 
        note.id === noteId ? { ...note, completed: !currentStatus } : note
      ));
      
      toast.success(`Note marked as ${!currentStatus ? 'completed' : 'incomplete'}`);
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleArchive = async (noteId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('hive_notes')
        .update({ archived: !currentStatus })
        .eq('id', noteId);

      if (error) throw error;
      
      setNotes(prev => prev.map(note => 
        note.id === noteId ? { ...note, archived: !currentStatus } : note
      ));
      
      toast.success(`Note ${!currentStatus ? 'archived' : 'unarchived'}`);
    } catch (error) {
      console.error('Error archiving note:', error);
      toast.error('Failed to archive note');
    }
  };

  const handleDelete = async () => {
    if (!deleteNote) return;

    try {
      const { error } = await supabase
        .from('hive_notes')
        .delete()
        .eq('id', deleteNote.id);

      if (error) throw error;
      
      setNotes(prev => prev.filter(note => note.id !== deleteNote.id));
      toast.success('Note deleted permanently');
      setDeleteNote(null);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const isDueSoon = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const threeDaysFromNow = new Date(today);
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    return date <= threeDaysFromNow;
  };

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="HivePad"
        subtitle="Quick notes and tasks for your organization"
        icon={StickyNote}
        actions={
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Note
          </Button>
        }
      />

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search notes, families, or students..."
            showShortcutHint={false}
          />
        </div>
        
        <div className="flex gap-2">
          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowUpDown className="h-4 w-4" />
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                { value: 'due_date_asc', label: 'Due Date (Soon → Later)', icon: ArrowUp },
                { value: 'due_date_desc', label: 'Due Date (Later → Soon)', icon: ArrowDown },
                { value: 'priority_desc', label: 'Priority (High → Low)', icon: ArrowDown },
                { value: 'priority_asc', label: 'Priority (Low → High)', icon: ArrowUp },
                { value: 'created_desc', label: 'Created (Newest First)', icon: ArrowDown },
                { value: 'created_asc', label: 'Created (Oldest First)', icon: ArrowUp },
                { value: 'title_asc', label: 'Title (A → Z)', icon: ArrowUp },
                { value: 'title_desc', label: 'Title (Z → A)', icon: ArrowDown },
              ].map(option => {
                const Icon = option.icon;
                return (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={sortOption === option.value}
                    onCheckedChange={() => setSortOption(option.value as SortOption)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {option.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                { value: 'active', label: 'Active Notes' },
                { value: 'archived', label: 'Archived Notes' },
                { value: 'all', label: 'All Notes' },
              ].map(option => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={filters.status === option.value}
                  onCheckedChange={() => setFilters(prev => ({ ...prev, status: option.value as FilterStatus }))}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
              
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Filter by Completion</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={filters.completed === false}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, completed: checked ? false : null }))}
              >
                Incomplete Only
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.completed === true}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, completed: checked ? true : null }))}
              >
                Completed Only
              </DropdownMenuCheckboxItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Filter by Association</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={filters.hasFamily === true}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, hasFamily: checked ? true : null }))}
              >
                Has Family
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.hasStudent === true}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, hasStudent: checked ? true : null }))}
              >
                Has Student
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.hasDueDate === true}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, hasDueDate: checked ? true : null }))}
              >
                Has Due Date
              </DropdownMenuCheckboxItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Filter by Priority</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
                { value: 'high', label: 'High', color: 'text-orange-600' },
                { value: 'medium', label: 'Medium', color: 'text-blue-600' },
                { value: 'low', label: 'Low', color: 'text-gray-600' },
              ].map(priority => (
                <DropdownMenuCheckboxItem
                  key={priority.value}
                  checked={filters.priority.includes(priority.value as any)}
                  onCheckedChange={(checked) => 
                    setFilters(prev => ({
                      ...prev,
                      priority: checked 
                        ? [...prev.priority, priority.value as any]
                        : prev.priority.filter(p => p !== priority.value)
                    }))
                  }
                >
                  <span className={priority.color}>{priority.label}</span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Notes Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          // Loading skeletons
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : filteredNotes.length === 0 ? (
          // Empty state
          <div className="col-span-full text-center py-12">
            <StickyNote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery.trim() ? 'No matching notes found' : 'No notes yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery.trim() 
                ? 'Try adjusting your search terms or filters'
                : 'Create your first note to get started with HivePad'
              }
            </p>
            {!searchQuery.trim() && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Note
              </Button>
            )}
          </div>
        ) : (
          // Notes cards
          filteredNotes.map((note) => (
            <Card key={note.id} className={`transition-all hover:shadow-md ${note.completed ? 'opacity-60' : ''} ${note.archived ? 'border-dashed' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-medium line-clamp-2 flex-1">
                    {note.title}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleComplete(note.id, note.completed)}
                    className="flex-shrink-0 ml-2"
                  >
                    {note.completed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {note.description && (
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {note.description}
                  </p>
                )}

                {/* Metadata */}
                <div className="space-y-1">
                  {/* Priority Badge */}
                  <div className="flex items-center gap-1">
                    <Badge 
                      className={`text-xs border ${getPriorityColor(note.priority)}`}
                      variant="outline"
                    >
                      {note.priority.charAt(0).toUpperCase() + note.priority.slice(1)} Priority
                    </Badge>
                  </div>
                  {note.families && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Users className="h-3 w-3" />
                      <span>{note.families.name}</span>
                    </div>
                  )}
                  {note.students && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <User className="h-3 w-3" />
                      <span>{note.students.first_name} {note.students.last_name}</span>
                    </div>
                  )}
                  {note.due_date && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <Badge 
                        variant={isDueSoon(note.due_date) ? "destructive" : "secondary"}
                        className="text-xs"
                      >
                        {formatDueDate(note.due_date)}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-1 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditNote(note)}
                    className="flex-1"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleArchive(note.id, note.archived)}
                    className="flex-1"
                  >
                    {note.archived ? (
                      <>
                        <ArchiveRestore className="h-3 w-3 mr-1" />
                        Restore
                      </>
                    ) : (
                      <>
                        <Archive className="h-3 w-3 mr-1" />
                        Archive
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteNote(note)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <HivePadModal
        open={showCreateModal || Boolean(editNote)}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            setEditNote(null);
          }
        }}
        editNote={editNote}
        onSuccess={loadNotes}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={Boolean(deleteNote)} onOpenChange={() => setDeleteNote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{deleteNote?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HivePad;